import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { GET_LIST, toggleSidebar, useListContext, useQuery, useRecordContext, useRefresh, useTranslate } from "react-admin"
import { Button, Grid, makeStyles, Menu, Paper } from "@material-ui/core"
import { ArrowDropDown } from "@material-ui/icons"
import { Alert, AlertTitle } from "@material-ui/lab"
import moment from "moment"

import { ListLoader } from "../../../../resources/officials/SelectOfficialsDialog"
import { CALENDAR_VIEWS, CalendarViewMenuItem, useCalendarView, useShowAvailabilities, useShowSlots, useShowTeamEvents } from "../../../../resources/events/EventViewSettings"
import { useOfficeSurfaces } from "../../../../resources/surfaces/useOfficeSurfaces"
import { SurfaceEventRow } from "../../../../resources/surfaceEvents/SurfaceEventRow"
import EventGroupedGrid from "../../../../resources/events/EventGroupedGrid"
import EventCalendar from "../../../../resources/events/EventCalendar"
import { SlotBuilderButton } from "../../../../resources/arenaslots/dialogs/slots/SlotBuilder"
import NoResults from "../../../NoResults"
import ListCard from "../../../cards/ListCard"
import { SquareAlert } from "../../../SquareAlert"
import { AssociationSchedulingProvider, useAssociationSchedulingContext } from "./AssociationSchedulingContext"
import { CalendarProvider } from "../../contexts/CalendarContext"
import { DragProvider } from "../../contexts/DragContext"
import { isAuthorized } from "../../../Authorize"
import { AssociationSchedulerFilters } from "./AssociationSchedulerFilters"
import { AddArenaOfficeButton } from "../../../../resources/arenaoffices/ArenaOfficeModal"
import { EditButton, RefreshCalendarButton } from "../league/SchedulerView"
import EventEditDialog from "../../EventEditDialog"

const useStyles = makeStyles(theme => ({
  menuButton: {
    marginLeft: theme.spacing(1.5),
  },
  menu: {
    minWidth: theme.spacing(32),
  },
  filterPaper: {
    margin: theme.spacing(1),
    marginLeft: 0,
    padding: theme.spacing(1),
  },
  filterIcon: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  showMoreButton: {
    display: 'flex',
    alignItems: 'center',
    marginInline: 'auto',
  }
}))

const useAllOfficeSurfaces = (officeId) => {
  const { data: officeSurfaces, loading: officeSurfacesLoading } = useOfficeSurfaces(officeId);

  const officeSurfaceIds = useMemo(() => (officeSurfaces || []).map(s => s.arenaId), [officeSurfaces]);

  const { data: surfaces, loading: surfacesLoading } = useQuery({
    type: GET_LIST,
    resource: 'surfaces',
    payload: {
      filter: { _scope: 'Tenant', effectiveSurfaces: officeSurfaceIds, _include: 'surfaces' },
      pagination: { page: 0, perPage: 9999 },
      sort: { field: ['name', 'path'], order: ['ASC', 'ASC'] }
    }
  }, {
    enabled: officeSurfaceIds.length > 0
  });

  return { data: surfaces, loading: surfacesLoading || officeSurfacesLoading }
}

const useViewDefaults = (calendarView) => {
  const { setFilters, filterValues, setPage, setPerPage, perPage } = useListContext();
  const hasInitialized = useRef({
    [CALENDAR_VIEWS.ASSOCIATION_CALENDAR]: false,
    [CALENDAR_VIEWS.ASSOCIATION_LIST_VIEW]: false,
  });

  useEffect(() => {
    if (calendarView === CALENDAR_VIEWS.ASSOCIATION_CALENDAR && !hasInitialized.current[calendarView]) {
      const startTime = filterValues?.startTime ? moment.utc(filterValues.startTime).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
      const endTime = moment.utc(startTime).add(5, "days").format("YYYY-MM-DD");

      setFilters({ ...filterValues, startTime, endTime }, null, false);
      setPage(1);
      setPerPage(9999);
      hasInitialized.current[calendarView] = true;
      hasInitialized.current[CALENDAR_VIEWS.ASSOCIATION_LIST_VIEW] = false;
    }

    if (calendarView === CALENDAR_VIEWS.ASSOCIATION_LIST_VIEW && !hasInitialized.current[calendarView]) {
      setPage(1);
      setPerPage(15);
      hasInitialized.current[calendarView] = true;
      hasInitialized.current[CALENDAR_VIEWS.ASSOCIATION_CALENDAR] = false;
    }
  }, [calendarView, filterValues, setFilters, perPage, setPage, setPerPage]);
}

const LoadMoreDaysButton = ({ days = 5 }) => {
  const classes = useStyles();
  const { setFilters, filterValues } = useListContext();
  const translate = useTranslate();

  const handleClick = () => {
    const endTime = moment.utc(filterValues.endTime).add(days, "days").format("YYYY-MM-DD");

    setFilters({ ...filterValues, endTime });
  }

  if (!filterValues?.endTime) return null;
  return <Button onClick={handleClick} color="primary" size="large" className={classes.showMoreButton}>
    {translate('resources.surfaceEvents.actions.load_more_days', { days })}
  </Button>
}

const ViewSettingsMenu = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const [ calendarView, setCalendarView ] = useCalendarView()

  const [ anchor, setAnchor ] = useState(null)
  const toggle = e => setAnchor(anchor => anchor ? !anchor : e.currentTarget)
  const onClose = () => setAnchor(null)

  return <>
    <Button onClick={toggle} color="secondary" variant="outlined" endIcon={<ArrowDropDown />} className={classes.menuButton}>
      {translate(`components.calendar.values.views.${calendarView}`)}
    </Button>
    <Menu
      classes={{ paper: classes.menu }}
      anchorEl={anchor}
      open={anchor != null}
      onClose={onClose}
    >
      <>
        <CalendarViewMenuItem view={CALENDAR_VIEWS.ASSOCIATION_CALENDAR} onClose={onClose} />
        <CalendarViewMenuItem view={CALENDAR_VIEWS.ASSOCIATION_LIST_VIEW} onClose={onClose} />
      </>
    </Menu>
  </>
}

const Calendar = ({ setEditEvent }) => {
  const translate = useTranslate();
  const { filterValues } = useListContext();
  const { setColOffset } = useAssociationSchedulingContext();

  useEffect(() => {
    setColOffset(0);
  }, [filterValues.arenaId, setColOffset]);

  const dates = useMemo(() => {
    const { startTime, endTime } = filterValues || {};
    if (!startTime || !endTime) return [];

    const start = moment.utc(startTime);
    const end = moment.utc(endTime);

    if (end.isBefore(start, 'day')) return [];

    return Array.from({ length: end.diff(start, 'days') + 1 }, (element, i) =>
      moment(start).add(i, 'days').format('YYYY-MM-DD')
    );
  }, [filterValues]);

  const popoverActions = useMemo(() => [
    <EditButton key="edit-event" setEvent={setEditEvent} />
  ], [ setEditEvent]);

  if (!dates.length) return <Alert severity="warning">{translate('components.calendar.validations.invalid_date_range')}</Alert>
  return <DragProvider>
    {dates.map(date => (
      <CalendarProvider key={date} date={date} popoverActions={popoverActions}>
        <EventCalendar />
      </CalendarProvider>
    ))}
    <LoadMoreDaysButton />
  </DragProvider>
}

const List = () => {
  const { total } = useListContext();

  if (total === 0) return <NoResults />
  return <>
    <ListLoader marginTop={0} />
    <EventGroupedGrid groupByDate groupByArena rowComponent={SurfaceEventRow} />
  </>
}

const View = ({ surfaces }) => {
  const [editEvent, setEditEvent] = useState(null);
  const classes = useStyles();
  const [calendarView] = useCalendarView();
  const refresh = useRefresh();
  useViewDefaults(calendarView);

  return <>
    <Grid container fullWidth spacing={0} style={{ flexWrap: 'nowrap' }}>
      <Grid item>
        <AssociationSchedulerFilters surfaces={surfaces} />
      </Grid>
      <Grid item style={{ flexGrow: 1, overflowY: 'none' }}>
        <Paper className={classes.filterPaper}>
          {CALENDAR_VIEWS.ASSOCIATION_CALENDAR === calendarView && <Calendar setEditEvent={setEditEvent} />}
          {CALENDAR_VIEWS.ASSOCIATION_LIST_VIEW === calendarView && <List />}
        </Paper>
      </Grid>
    </Grid>

    <EventEditDialog
      editEvent={editEvent}
      onSuccess={refresh}
      handleClose={() => setEditEvent(null)}
    />
  </>
}

const NoOfficeSurfaceAlert = () => {
  const office = useRecordContext()
  const translate = useTranslate()
  const { loading } = useListContext()

  const canAddArenaOffice = isAuthorized(office, 'arenaoffices', 'create');

  if (loading) return null
  return <SquareAlert
    severity="warning"
    action={
      canAddArenaOffice && <AddArenaOfficeButton initialValues={{ officeId: office?.id }} target="officeId" showStartIcon={false} />
    }
  >
    <AlertTitle>{translate('components.calendar.alerts.no_office_surfaces_title')}</AlertTitle>
    <span>{translate('components.calendar.alerts.no_office_surfaces')}</span>
  </SquareAlert>
}

export default ({ ...props }) => {
  const office = useRecordContext()
  const dispatch = useDispatch();
  const isSidebarOpen = useSelector(state => state.admin.ui.sidebarOpen);
  const { data: surfaces, loading } = useAllOfficeSurfaces(office?.id);
  const sidebarWasOpenRef = useRef(false);

  const [calendarView, setCalendarView] = useCalendarView();
  const [showTeamEvents, setShowTeamEvents] = useShowTeamEvents();
  const [showSlots, setShowSlots] = useShowSlots();
  const [showAvailabilities, setShowAvailabilities] = useShowAvailabilities();

  const canBuildSlots = isAuthorized(office, 'arenaslots', 'buildSlots');

  useEffect(() => {
    if (isSidebarOpen) {
      sidebarWasOpenRef.current = true;
      dispatch(toggleSidebar())
    }

    setCalendarView(CALENDAR_VIEWS.ASSOCIATION_LIST_VIEW);

    return () => {
      if (sidebarWasOpenRef.current) {
        dispatch(toggleSidebar())
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (calendarView !== CALENDAR_VIEWS.ASSOCIATION_CALENDAR) return;

    setShowTeamEvents(true);
    setShowSlots(true);
    setShowAvailabilities(true);
  }, [calendarView, setShowAvailabilities, setShowSlots, setShowTeamEvents])

  const actions = [
    canBuildSlots && <SlotBuilderButton key="slot-builder" initialValues={{ officeId: office?.id }} disabled={!surfaces?.length} />,
    <ViewSettingsMenu key="view-settings" />,
    <RefreshCalendarButton key="refresh" />,
  ]

  if (![CALENDAR_VIEWS.ASSOCIATION_CALENDAR, CALENDAR_VIEWS.ASSOCIATION_LIST_VIEW].includes(calendarView)) return null;
  return <AssociationSchedulingProvider surfaces={surfaces} office={office}>
    <ListCard
      basePath="/surfaceEvents"
      resource="surfaceEvents"
      actions={actions}
      alert={!loading && !(surfaces || []).length && <NoOfficeSurfaceAlert />}
      sort={{ field: 'startTime', order: 'ASC' }}
      filter={{ _scope: 'Tenant', effectiveSurfaces: false, 'surface.offices.id': office?.id }}
      perPage={15}
      noResultsText={false}
      rowsPerPageOptions={[15, 25, 50, 100]}
    >
      <View />
    </ListCard>
  </AssociationSchedulingProvider>
}
